<template>
	<div class="relative min-h-[100dvh] w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
		<!-- 🌗 Dark/Light Mode Toggle (Mobile - above logo) -->
		<button
		@click="themeStore.toggleTheme()"
		class="md:hidden absolute top-4 right-4 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition z-10"
		aria-label="Toggle dark mode"
		>
			<!-- Sun Icon -->
			<Sun v-if="isDarkMode" h-6 w-6/>

			<!-- Moon Icon -->
			<Moon v-else h-6 w-6/>
		</button>
		<div class="w-full min-h-[100dvh] flex flex-col md:flex-row bg-white dark:bg-gray-800 overflow-hidden">
			<!-- Left Branding (Desktop only) -->
			<div
				class="hidden md:flex w-1/2 bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 items-center justify-center p-8"
			>
				<div class="text-center text-white space-y-6">
					<img
						src='images/main-logo-dark.png'
						alt="App Logo"
						class="mx-auto h-24 w-[18rem] object-contain drop-shadow-md"
					/>
					<h2 class="text-3xl font-bold">{{ appTitle }}</h2>
					<!-- <p class="text-lg opacity-90">Reset your password securely</p> -->
				</div>
			</div>

			<!-- Right Form Section -->
			<div class="flex flex-col justify-center items-center w-full md:w-1/2 p-4 sm:p-8 lg:p-12 relative flex-1 md:flex-none overflow-hidden">
				<!-- Mobile Header - Logo and Title (visible only on mobile) -->
				<div class="flex md:hidden flex-col items-center justify-center pt-4 pb-8 px-4 shrink-0">
					<img :src="isDarkMode ? 'images/main-logo-dark.png' : 'images/main-logo.png'" alt="App Logo" class="h-16 w-auto max-w-[18rem] object-contain drop-shadow-sm" />
				</div>
				
				<!-- 🌗 Dark/Light Mode Toggle (Desktop) -->
				<button
				@click="themeStore.toggleTheme()"
				class="hidden md:block absolute top-6 right-6 p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
				aria-label="Toggle dark mode"
				>
					<!-- Sun Icon -->
					<Sun v-if="isDarkMode" h-6 w-6/>

					<!-- Moon Icon -->
					<Moon v-else h-6 w-6/>
				</button>

				<!-- Form Container -->
				<div class="w-full max-w-md space-y-4 md:space-y-8">
					<div class="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700">
						<h2
						v-if="step !== 4"
						class="text-2xl sm:text-3xl font-extrabold text-primary dark:text-primary-dark-text mb-6 text-center"
						>
						{{ step === 1 ? 'Forgot Your Password?' : step === 2 ? 'Verify Code' : 'Reset Your Password' }}
						</h2>
						<!-- <p class="block text-sm text-red-500 dark:text-red-400 mb-5 justify-center text-center font-medium animate-none">Note: Password reset in demo is disabled</p> -->

						<!-- Step 1 -->
						<form v-if="step === 1" class="space-y-6" @submit.prevent="sendOtp">
							<div>
								<label for="email" class="label">Email address</label>
								<input
								id="email"
								type="email"
								v-model="email"
								required
								class="input w-full"
								placeholder="Enter your email address"
								/>
							</div>
							
							<button
							:disabled="!email || loading"
							type="submit"
							class="w-full flex justify-center py-3 bg-primary text-white font-semibold rounded-lg shadow-md transition hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
							>
							<LoaderCircle v-if="loading" class="w-5 h-5 mr-2 animate-spin" />
							{{ loading ? 'Sending OTP...' : 'Send OTP' }}
							</button>

							<a
								class="flex justify-center font-bold text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover hover:underline"
								href="/portal/login"
								>
								Back to login
							</a>
						</form>

						<!-- Step 2 -->
						<form v-else-if="step === 2" class="space-y-6" @submit.prevent="verifyOtp">
							<div>
								<div class="flex justify-between items-center mb-1">
									<label for="otp" class="label mb-0">Enter OTP</label>
									<span :key="timeLeft" class="text-sm font-medium inline-block" :class="timeLeft > 0 ? 'text-primary dark:text-primary-light' : 'text-red-500'">
										{{ timeLeft > 0 ? `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}` : 'Expired' }}
									</span>
								</div>
								<div class="flex justify-center gap-2 mt-4">
									<input
										v-for="(digit, index) in otpDigits"
										:key="index"
										ref="otpInputsRef"
										type="text"
										inputmode="numeric"
										maxlength="1"
										v-model="otpDigits[index]"
										@input="onOtpInput(index, $event)"
										@keydown="onOtpKeyDown(index, $event)"
										@paste="onOtpPaste"
										:disabled="timeLeft === 0 || loading"
										class="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-0 focus:border-primary dark:focus:border-primary-light disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900 transition-colors"
									/>
								</div>
							</div>

							<div v-if="timeLeft === 0" class="text-sm text-center text-red-500 font-medium">
								Your OTP has expired. 
								<a href="#" @click.prevent="step = 1" class="text-primary hover:underline">Click here to request a new one</a>.
							</div>

							<button
								type="submit"
								:disabled="loading || timeLeft === 0"
								class="w-full py-3 bg-gradient-to-br from-primary to-primary-hover/50 hover:bg-secondary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-md transition"
							>
								{{ loading ? 'Verifying OTP...' : 'Verify OTP' }}
							</button>
						</form>

						<!-- Step 3 -->
						<form v-else-if="step === 3" class="space-y-6" @submit.prevent="changePassword">
							<div>
								<label for="new-password" class="label">New Password</label>
								<div class="relative">
									<input
									id="new-password"
									:type="showNewPassword ? 'text' : 'password'"
									v-model="newPassword"
									class="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none pr-10"
									placeholder="********"
									/>
									<button
										type="button"
										@click="showNewPassword = !showNewPassword"
										class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
									>
										<component :is="showNewPassword ? EyeOff : Eye" class="h-5 w-5" />
									</button>
								</div>
							</div>

							<div>
								<label for="confirm-password" class="label">Confirm Password</label>
								<div class="relative">
									<input
									id="confirm-password"
									:type="showConfirmPassword ? 'text' : 'password'"
									v-model="confirmPassword"
									class="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none pr-10"
									placeholder="********"
									/>
									<button
										type="button"
										@click="showConfirmPassword = !showConfirmPassword"
										class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
									>
										<component :is="showConfirmPassword ? EyeOff : Eye" class="h-5 w-5" />
									</button>
								</div>
							</div>

							<button
								type="submit"
								:disabled="loading"
								class="w-full py-3 bg-gradient-to-r from-primary to-primary-hover/50 hover:bg-secondary hover:opacity-90 text-white font-semibold rounded-lg shadow-md transition"
							>
								{{ loading ? 'Resetting...' : 'Reset Password' }}
							</button>
						</form>

						<!-- Step 4 -->
						<div
						v-else
						class="flex flex-col items-center justify-center text-center h-full space-y-2"
						>
							<h3 class="text-xl font-semibold text-green-600">
								Password Reset Successful
							</h3>
							<p class="text-slate-600 dark:text-slate-400">
								You can now
								<a href="/portal/login" class="text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover font-bold hover:underline">log in</a>
								with your new password.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useThemeStore } from '@/stores/theme'
import { useTitle } from '@/composables/ui/useTitle'
import { useForgotPassword } from '@/composables/auth/useForgotPassword'
import { Sun, Moon, LoaderCircle, Eye, EyeOff } from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')

const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

useTitle(`${appTitle.value} - Forgot Password`)

const {
	email,
	otp,
	newPassword,
	confirmPassword,
	step,
	loading,
	timeLeft,
	sendOtp,
	verifyOtp,
	changePassword
} = useForgotPassword()

const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// ─── OTP BOX LOGIC ───
const otpDigits = ref(['', '', '', '', '', ''])
const otpInputsRef = ref<HTMLInputElement[]>([])

const onOtpInput = (index: number, event: Event) => {
	const target = event.target as HTMLInputElement
	let val = target.value

	// Only allow numbers
	if (!/^\d*$/.test(val)) {
		otpDigits.value[index] = ''
		val = ''
	}

	// Move to next input if filled
	if (val && index < 5) {
		otpInputsRef.value[index + 1]?.focus()
	}

	otp.value = otpDigits.value.join('')
}

const onOtpKeyDown = (index: number, event: KeyboardEvent) => {
	// Handle Arrow Keys for traversing
	if (event.key === 'ArrowLeft' && index > 0) {
		event.preventDefault()
		otpInputsRef.value[index - 1]?.focus()
	} else if (event.key === 'ArrowRight' && index < 5) {
		event.preventDefault()
		otpInputsRef.value[index + 1]?.focus()
	}
	
	// Handle Backspace
	else if (event.key === 'Backspace') {
		if (!otpDigits.value[index] && index > 0) {
			// If current is empty, move back and clear the previous one
			otpDigits.value[index - 1] = ''
			otpInputsRef.value[index - 1]?.focus()
			otp.value = otpDigits.value.join('')
		} else {
			// If current is NOT empty, ensure sync
			setTimeout(() => {
				otp.value = otpDigits.value.join('')
			}, 0)
		}
	}
	
	// Handle Delete key
	else if (event.key === 'Delete') {
		otpDigits.value[index] = ''
		otp.value = otpDigits.value.join('')
	}
}

const onOtpPaste = (event: ClipboardEvent) => {
	event.preventDefault()
	const pastedData = event.clipboardData?.getData('text/plain')
	if (!pastedData) return
	
	const numbers = pastedData.replace(/\D/g, '').slice(0, 6)
	numbers.split('').forEach((num, i) => {
		if (i < 6) otpDigits.value[i] = num
	})
	
	otp.value = otpDigits.value.join('')
	
	// Focus last filled input or next empty
	const nextEmptyIndex = otpDigits.value.findIndex(d => !d)
	const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex
	otpInputsRef.value[focusIndex]?.focus()
}

// ─── PREVENT REFRESH ON STEP 2 & 3 ───
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
	if (step.value === 2 || step.value === 3) {
		e.preventDefault()
		e.returnValue = '' // Required for Chrome to show the prompt
	}
}

onMounted(() => {
	window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
	window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>